import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Row, Col, Collapse } from 'react-bootstrap';
import Sidebar from '../Components/Siderbar';
import Footer from '../Components/Footer';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import '../Customize_Css/AddVendor.css';
import '../Customize_Css/PriceHeads.css';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const PriceHeads = () => {
  const navigate = useNavigate();
  const [priceHeads, setPriceHeads] = useState({});
  const [openSections, setOpenSections] = useState({});
  const [globalBaseRate, setGlobalBaseRate] = useState(0); 
  const epicxTenantId = localStorage.getItem('epicxTenantId');
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const AuthorizationToken = localStorage.getItem('token');

  const priceHeadsConfig = {
    deliveryCharge: { formula: 'baseRate', fields: ['baseRate'] },
    distanceBasedCharge: { formula: 'distance * ratePerKm', fields: ['distance', 'ratePerKm'] },
    outstationDeliveryCharge: { formula: 'baseRate + (distance * ratePerKm)', fields: ['baseRate', 'distance', 'ratePerKm'] },
    nightDeliveryCharge: { formula: 'baseRate * nightMultiplier', fields: ['baseRate', 'nightMultiplier'] },
    expressDeliveryCharge: { formula: 'baseRate * expressMultiplier', fields: ['baseRate', 'expressMultiplier'] },
    handlingCharge: { formula: 'weight * ratePerKg', fields: ['weight', 'ratePerKg'] },
    packagingCharge: { formula: 'volume * ratePerCubicMeter', fields: ['volume', 'ratePerCubicMeter'] },
    codCharge: { formula: 'orderValue * codPercentage', fields: ['orderValue', 'codPercentage'] },
    platformFee: { formula: 'fixedFee', fields: ['fixedFee'] },
    highDemandSurgeCharge: { formula: 'baseRate * surgeMultiplier', fields: ['baseRate', 'surgeMultiplier'] },
    holidayWeekendSurgeCharge: { formula: 'baseRate * holidayMultiplier', fields: ['baseRate', 'holidayMultiplier'] },
    orderWeightCharge: { formula: 'weight * weightRate', fields: ['weight', 'weightRate'] },
    orderVolumeCharge: { formula: 'volume * volumeRate', fields: ['volume', 'volumeRate'] },
    remoteAreaCharge: { formula: 'baseRate + additionalFee', fields: ['baseRate', 'additionalFee'] },
    locationBasedCharge: { formula: 'distance * locationRate', fields: ['distance', 'locationRate'] },
    membershipDiscount: { formula: 'baseRate * discountPercentage', fields: ['baseRate', 'discountPercentage'] },
    discountedDeliveryCharge: { formula: 'baseRate - discountAmount', fields: ['baseRate', 'discountAmount'] },
    ecoFriendlyDelivery: { formula: 'baseRate + ecoPremium', fields: ['baseRate', 'ecoPremium'] },
    minimumOrderCharge: { formula: 'minimumFee', fields: ['minimumFee'] }
  };

  useEffect(() => {
    fetchPriceHeads();
  }, []);

  const fetchPriceHeads = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/priceheads/api/priceHeads/${epicxTenantId}`,
        {
          headers: {
              Authorization: `Bearer ${AuthorizationToken}`,
            },
          }
      );
      setPriceHeads(response.data);
    } catch (error) {
      console.error("Error fetching price heads:", error);
    }
  };

  const handleToggle = (field) => {
    setPriceHeads({
      ...priceHeads,
      [field]: { 
        ...priceHeads[field], 
        enable: !priceHeads[field]?.enable,
        baseRate: !priceHeads[field]?.enable ? globalBaseRate : priceHeads[field]?.baseRate // Set global baseRate when enabling
      }
    });
  };

  const handleFieldChange = (priceHead, field, value) => {
    setPriceHeads({
      ...priceHeads,
      [priceHead]: { ...priceHeads[priceHead], [field]: parseFloat(value) || 0 }
    });
  };

  const handleGlobalBaseRateChange = (value) => {
    const newBaseRate = parseFloat(value) || 0;
    setGlobalBaseRate(newBaseRate);
    // Optionally update all enabled price heads with the new global baseRate
    const updatedPriceHeads = { ...priceHeads };
    Object.keys(updatedPriceHeads).forEach((field) => {
      if (updatedPriceHeads[field].enable && priceHeadsConfig[field].fields.includes('baseRate')) {
        updatedPriceHeads[field].baseRate = newBaseRate;
      }
    });
    setPriceHeads(updatedPriceHeads);
  };

  const calculateResult = (priceHead) => {
    const config = priceHeadsConfig[priceHead];
    const data = priceHeads[priceHead] || {};
    if (!data.enable) return 0;

    switch (priceHead) {
      case 'deliveryCharge': return data.baseRate || globalBaseRate;
      case 'distanceBasedCharge': return (data.distance || 0) * (data.ratePerKm || 0);
      case 'outstationDeliveryCharge': return (data.baseRate || globalBaseRate) + ((data.distance || 0) * (data.ratePerKm || 0));
      case 'nightDeliveryCharge': return (data.baseRate || globalBaseRate) * (data.nightMultiplier || 0);
      case 'expressDeliveryCharge': return (data.baseRate || globalBaseRate) * (data.expressMultiplier || 0);
      case 'handlingCharge': return (data.weight || 0) * (data.ratePerKg || 0);
      case 'packagingCharge': return (data.volume || 0) * (data.ratePerCubicMeter || 0);
      case 'codCharge': return (data.orderValue || 0) * (data.codPercentage || 0);
      case 'platformFee': return data.fixedFee || 0;
      case 'highDemandSurgeCharge': return (data.baseRate || globalBaseRate) * (data.surgeMultiplier || 0);
      case 'holidayWeekendSurgeCharge': return (data.baseRate || globalBaseRate) * (data.holidayMultiplier || 0);
      case 'orderWeightCharge': return (data.weight || 0) * (data.weightRate || 0);
      case 'orderVolumeCharge': return (data.volume || 0) * (data.volumeRate || 0);
      case 'remoteAreaCharge': return (data.baseRate || globalBaseRate) + (data.additionalFee || 0);
      case 'locationBasedCharge': return (data.distance || 0) * (data.locationRate || 0);
      case 'membershipDiscount': return (data.baseRate || globalBaseRate) * (data.discountPercentage || 0);
      case 'discountedDeliveryCharge': return (data.baseRate || globalBaseRate) - (data.discountAmount || 0);
      case 'ecoFriendlyDelivery': return (data.baseRate || globalBaseRate) + (data.ecoPremium || 0);
      case 'minimumOrderCharge': return data.minimumFee || 0;
      default: return 0;
    }
  };

  const handleCancel = () => {
    setPriceHeads({});
    setGlobalBaseRate(0);
    toast.info('All fields have been reset');
  };

  const handleSubmit = async () => {
    try {
      const submissionData = {};
      Object.keys(priceHeadsConfig).forEach((field) => {
        if (priceHeads[field]?.enable) {
          submissionData[field] = {
            ...priceHeads[field],
            calculatedAmount: calculateResult(field),
          };
        }
      });
  
      if (Object.keys(submissionData).length === 0) {
        toast.warn("No price heads enabled to submit!");
        return;
      }
  
      console.log("Submitting:", submissionData);
  
      const response = await axios.post(
        `${BASE_URL}/priceheads/api/priceHeads/${epicxTenantId}`,
        { priceHeads: submissionData },
        {
          headers: {
            Authorization: `Bearer ${AuthorizationToken}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      console.log("Response:", response.data);
      toast.success("Price Heads Saved Successfully");
      setPriceHeads({});
    } catch (error) {
      console.error("Error submitting data:", error.response?.data || error.message);
      toast.error("Failed to Submit Price Heads!");
    }
  };
  const toggleSection = (field) => {
    setOpenSections({ ...openSections, [field]: !openSections[field] });
  };

  return (
    <>
      <Sidebar />
      <div className="vendor-form-page">
        <div className="vendor-form-shell">
          <header className="vendor-form-hero">
            <div>
              <p className="vendor-form-kicker">Sales</p>
              <h1>Price Heads</h1>
              <p>Set the base rate and turn on the charges you want to save.</p>
            </div>
            <button type="button" className="vendor-form-back-btn" onClick={() => navigate('/priceeview')}>Back to Price Heads</button>
          </header>
          <div className="vendor-form-card price-heads-container">
            <div className="vendor-form-card-head">
              <div>
                <h2>Charges</h2>
                <p>Global base rate and the charges applied to an order.</p>
              </div>
            </div>
            <div className="vendor-form-card-body">
              {/* Global Base Rate Input */}
              <Form.Group className="mb-4">
                <Form.Label className="field-label">Global Base Rate</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter Global Base Rate"
                  value={globalBaseRate}
                  onChange={(e) => handleGlobalBaseRateChange(e.target.value)}
                  step="0.1"
                  className="custom-input"
                />
              </Form.Group>

              <Row>
                {Object.entries(priceHeadsConfig).map(([field, config], idx) => (
                  <Col md={6} key={idx} className="mb-4">
                    <div className={`price-card ${priceHeads[field]?.enable ? 'active' : ''}`}>
                      <div className="price-card-header" onClick={() => toggleSection(field)}>
                        <div className="d-flex justify-content-between align-items-center w-100">
                          <h5 className="mb-0 text-capitalize">
                            {field.replace(/([A-Z])/g, ' $1').trim()}
                            {priceHeads[field]?.enable && !openSections[field] && calculateResult(field) > 0 && (
                              <span className="result-closed ml-2">₹{calculateResult(field).toFixed(2)}</span>
                            )}
                          </h5>
                          <div className="d-flex align-items-center">
                            <Form.Check
                              type="switch"
                              className="custom-switch ml-3"
                              checked={priceHeads[field]?.enable || false}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleToggle(field);
                              }}
                            />
                            {openSections[field] ? <FaChevronUp className="ml-2" /> : <FaChevronDown className="ml-2" />}
                          </div>
                        </div>
                      </div>
                      <Collapse in={openSections[field]}>
                        <div className="price-card-body">
                          <p className="formula-text">Formula: <span>{config.formula}</span></p>
                          {config.fields.map((inputField) => (
                            <Form.Group key={inputField} className="mb-3">
                              <Form.Label className="field-label">
                                {inputField.replace(/([A-Z])/g, ' $1').trim()}
                              </Form.Label>
                              <Form.Control
                                type="number"
                                placeholder={`Enter ${inputField.replace(/([A-Z])/g, ' $1').trim()}`}
                                value={
                                  inputField === 'baseRate' && !priceHeads[field]?.baseRate
                                    ? globalBaseRate
                                    : priceHeads[field]?.[inputField] || ''
                                }
                                onChange={(e) => handleFieldChange(field, inputField, e.target.value)}
                                disabled={!priceHeads[field]?.enable}
                                step={inputField.includes('Percentage') ? '0.01' : '0.1'}
                                className="custom-input"
                              />
                            </Form.Group>
                          ))}
                          <div className="result-box">
                            <span>Result:</span>
                            <strong>₹{calculateResult(field).toFixed(2)}</strong>
                          </div>
                        </div>
                      </Collapse>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
          <div className="vendor-form-actions">
            <button type="button" className="vendor-form-cancel" onClick={handleCancel}>Cancel</button>
            <button type="button" className="vendor-form-submit" onClick={handleSubmit}>Save and Submit</button>
          </div>
        </div>
        <div className="vendor-form-footer">
          <Footer />
        </div>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        rtl={true}
        draggable
        pauseOnHover
        style={{ zIndex: 9999 }}
      />
    </>
  );
};

export default PriceHeads;